import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Icon from 'components/AppIcon';
import {
  useGetPeriodsByUserIdQuery,
  useAddPeriodMutation,
  useUpdatePeriodMutation,
  useDeletePeriodMutation,
} from 'redux/features/employeePeriodsApi';

const PERIOD_TYPE_OPTIONS = [
  { value: 'INTERNSHIP', label: 'Internship' },
  { value: 'PROBATION', label: 'Probation' },
  { value: 'TRAINEE', label: 'Training' },
  { value: 'EMPLOYMENT', label: 'Employment' },
  { value: 'NOTICE', label: 'Notice Period' },
];

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Completed', label: 'Completed' },
];

const emptyPeriod = {
  period_type: '',
  start_date: '',
  end_date: '',
  status: 'Active',
  remark: '',
};

const formatDateKey = (key) =>
  new Date(`${key}T00:00:00`).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const typeLabel = (value) => PERIOD_TYPE_OPTIONS.find((o) => o.value === value)?.label || value;

/**
 * Date errors for a period row, mirroring the backend rules: the end date can't be before
 * the start date, and the period can't overlap the employee's other saved periods.
 * Dates are YYYY-MM-DD strings, so they compare correctly as text. No end date = still running.
 */
const getDateErrors = (row, savedPeriods) => {
  const errors = {};
  const start = row.start_date;
  const end = row.end_date || null;
  if (!start) return errors;

  if (end && end < start) {
    errors.end_date = 'End date cannot be before the start date';
    return errors;
  }

  for (const other of savedPeriods) {
    if (other.id === row.id) continue;
    const otherStart = other.start_date?.split('T')[0];
    const otherEnd = other.end_date ? other.end_date.split('T')[0] : null;
    if (!otherStart) continue;

    const overlaps = (!otherEnd || start <= otherEnd) && (!end || otherStart <= end);
    if (!overlaps) continue;

    const label = typeLabel(other.period_type);
    if (otherStart <= start) {
      errors.start_date = otherEnd
        ? `Must be after the previous ${label} period's end date (${formatDateKey(otherEnd)})`
        : `The ${label} period starting ${formatDateKey(otherStart)} has no end date`;
    } else {
      errors.end_date = end
        ? `Must be before the next ${label} period's start date (${formatDateKey(otherStart)})`
        : `Required: the next ${label} period starts on ${formatDateKey(otherStart)}`;
    }
    break;
  }

  return errors;
};

const EmployeePeriods = ({ userId }) => {
  const {
    data: periods = [],
    isLoading,
    refetch,
  } = useGetPeriodsByUserIdQuery(userId, { skip: !userId });

  const [addPeriod, { isLoading: isAdding }] = useAddPeriodMutation();
  const [updatePeriod, { isLoading: isUpdating }] = useUpdatePeriodMutation();
  const [deletePeriod, { isLoading: isDeleting }] = useDeletePeriodMutation();

  const [rows, setRows] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [errors, setErrors] = useState({});

  const isSaving = isAdding || isUpdating;

  useEffect(() => {
    if (periods && periods.length > 0) {
      const mapped = periods.map((p) => ({
        id: p.id,
        period_type: p.period_type || '',
        start_date: p.start_date ? p.start_date.split('T')[0] : '',
        end_date: p.end_date ? p.end_date.split('T')[0] : '',
        status: p.status || 'Active',
        remark: p.remark || '',
        isNew: false,
        isDirty: false,
      }));
      setRows(mapped);
    } else {
      setRows([]);
    }
  }, [periods]);

  const handleAddNew = () => {
    const newRow = {
      ...emptyPeriod,
      id: `temp-${Date.now()}`,
      isNew: true,
      isDirty: true,
    };
    setRows((prev) => [newRow, ...prev]);
    setEditingRowId(newRow.id);
    setErrors({});
  };

  const handleRowChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value, isDirty: true } : row)),
    );
    const errorKey = `${id}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[errorKey];
        return updated;
      });
    }
  };

  const validateRow = (row) => {
    const newErrors = {};
    if (!row.period_type) {
      newErrors[`${row.id}_period_type`] = 'Period type is required';
    }
    if (!row.start_date) {
      newErrors[`${row.id}_start_date`] = 'Start date is required';
    }
    const dateErrors = getDateErrors(row, periods);
    Object.entries(dateErrors).forEach(([field, message]) => {
      newErrors[`${row.id}_${field}`] = message;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (row) => {
    if (!validateRow(row)) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    const payload = {
      period_type: row.period_type,
      start_date: row.start_date,
      end_date: row.end_date || null,
      status: row.status,
      remark: row.remark || null,
    };

    try {
      if (row.isNew) {
        await addPeriod({ user_id: parseInt(userId, 10), ...payload }).unwrap();
        toast.success('Period added successfully');
      } else {
        await updatePeriod({ id: row.id, ...payload }).unwrap();
        toast.success('Period updated successfully');
      }
      setEditingRowId(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to save period');
    }
  };

  const handleDelete = async (row) => {
    if (row.isNew) {
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setEditingRowId(null);
      return;
    }

    if (!window.confirm('Delete this period? This cannot be undone.')) return;

    try {
      await deletePeriod(row.id).unwrap();
      toast.success('Period deleted successfully');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete period');
    }
  };

  const handleEdit = (id) => {
    setEditingRowId(id);
    setErrors({});
  };

  const handleCancelEdit = (row) => {
    if (row.isNew) {
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } else {
      const original = periods.find((p) => p.id === row.id);
      if (original) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  period_type: original.period_type || '',
                  start_date: original.start_date ? original.start_date.split('T')[0] : '',
                  end_date: original.end_date ? original.end_date.split('T')[0] : '',
                  status: original.status || 'Active',
                  remark: original.remark || '',
                  isDirty: false,
                }
              : r,
          ),
        );
      }
    }
    setEditingRowId(null);
    setErrors({});
  };

  const inputBase =
    'w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors';
  const inputClasses = (hasError) =>
    `${inputBase} ${hasError ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-300'}`;

  const getTypeLabel = (value) =>
    PERIOD_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || '-';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
        <span className="ml-3 text-gray-600">Loading periods...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 animate-fadeIn">
      {/* Header */}
      <div className="px-5 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2.5">
          <Icon name="Calendar" size={20} color="var(--color-primary)" />
          <span>Training & Employment Dates</span>
        </h2>
        <button
          type="button"
          onClick={handleAddNew}
          disabled={editingRowId !== null}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          <Icon name="Plus" size={16} className="mr-1.5" />
          Add New Period
        </button>
      </div>

      <div className="p-4 sm:p-6">
        {rows.length === 0 ? (
          <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/70">
            <Icon name="Calendar" size={36} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-600">No periods added yet</p>
            <p className="text-xs text-gray-500 mt-1">
              Click &quot;Add New Period&quot; to get started
            </p>
          </div>
        ) : (
          <>
            {/* --- Desktop Table --- */}
            <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[180px]">
                      Period Type *
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[155px]">
                      Start Date *
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[155px]">
                      End Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[150px]">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Remark
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((row, index) => {
                    const isEditing = editingRowId === row.id;
                    const typeError = errors[`${row.id}_period_type`];
                    const startError = errors[`${row.id}_start_date`];
                    const endError = errors[`${row.id}_end_date`];

                    if (isEditing) {
                      return (
                        <>
                          {/* Edit Row 1 */}
                          <tr key={`${row.id}-edit-1`} className="bg-blue-50/40">
                            <td className="px-4 pt-4 pb-2 text-sm text-gray-500 align-top">
                              {index + 1}
                            </td>
                            {/* Period Type */}
                            <td className="px-3 pt-4 pb-2 align-top">
                              <label className="block text-[11px] font-medium text-gray-600 mb-1">
                                Type *
                              </label>
                              <select
                                value={row.period_type}
                                onChange={(e) =>
                                  handleRowChange(row.id, 'period_type', e.target.value)
                                }
                                className={inputClasses(!!typeError)}
                              >
                                <option value="">Select Type</option>
                                {PERIOD_TYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                              {typeError && (
                                <p className="mt-1 text-xs text-red-600">{typeError}</p>
                              )}
                            </td>
                            {/* Start Date */}
                            <td className="px-3 pt-4 pb-2 align-top">
                              <label className="block text-[11px] font-medium text-gray-600 mb-1">
                                Start *
                              </label>
                              <input
                                type="date"
                                value={row.start_date}
                                onChange={(e) =>
                                  handleRowChange(row.id, 'start_date', e.target.value)
                                }
                                className={inputClasses(!!startError)}
                              />
                              {startError && (
                                <p className="mt-1 text-xs text-red-600">{startError}</p>
                              )}
                            </td>
                            {/* End Date */}
                            <td className="px-3 pt-4 pb-2 align-top">
                              <label className="block text-[11px] font-medium text-gray-600 mb-1">
                                End
                              </label>
                              <input
                                type="date"
                                value={row.end_date}
                                onChange={(e) =>
                                  handleRowChange(row.id, 'end_date', e.target.value)
                                }
                                className={inputClasses(!!endError)}
                              />
                              {endError && <p className="mt-1 text-xs text-red-600">{endError}</p>}
                            </td>
                            {/* Status */}
                            <td className="px-3 pt-4 pb-2 align-top">
                              <label className="block text-[11px] font-medium text-gray-600 mb-1">
                                Status
                              </label>
                              <select
                                value={row.status}
                                onChange={(e) => handleRowChange(row.id, 'status', e.target.value)}
                                className={inputClasses(false)}
                              >
                                {STATUS_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td colSpan={2} className="px-4 pt-4 pb-2 text-right align-top">
                              <span className="text-xs text-blue-700 font-medium">
                                {row.isNew ? 'New period' : 'Editing'}
                              </span>
                            </td>
                          </tr>
                          {/* Edit Row 2: Remark + Actions */}
                          <tr key={`${row.id}-edit-2`} className="bg-blue-50/40">
                            <td></td>
                            <td colSpan={4} className="px-3 pb-4 pt-1">
                              <label className="block text-[11px] font-medium text-gray-600 mb-1">
                                Remark
                              </label>
                              <input
                                type="text"
                                value={row.remark}
                                onChange={(e) => handleRowChange(row.id, 'remark', e.target.value)}
                                className={inputClasses(false)}
                                placeholder="Optional remark / notes"
                              />
                            </td>
                            <td colSpan={2} className="mt-4 text-right">
                              <td></td>
                              <div className="flex items-end justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSave(row)}
                                  disabled={isSaving}
                                  className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors"
                                >
                                  <Icon name="Check" size={15} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleCancelEdit(row)}
                                  className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                                >
                                  <Icon name="X" size={15} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(row)}
                                  disabled={isDeleting}
                                  className="inline-flex items-center p-2 text-red-600 hover:bg-red-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50"
                                  title="Delete"
                                >
                                  <Icon name="Trash" size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </>
                      );
                    }

                    // Read-only row
                    return (
                      <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 text-sm text-gray-500">{index + 1}</td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">
                          {getTypeLabel(row.period_type)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-800">{row.start_date || '-'}</td>
                        <td className="px-4 py-4 text-sm text-gray-800">{row.end_date || '-'}</td>
                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              row.status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td
                          className="px-4 py-4 text-sm text-gray-600 max-w-xs truncate"
                          title={row.remark}
                        >
                          {row.remark || '-'}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleEdit(row.id)}
                              disabled={editingRowId !== null}
                              className="inline-flex items-center p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title="Edit"
                            >
                              <Icon name="Pencil" size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row)}
                              disabled={editingRowId !== null || isDeleting}
                              className="inline-flex items-center p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title="Delete"
                            >
                              <Icon name="Trash" size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* --- Mobile / Tablet Card List --- */}
            <div className="lg:hidden space-y-3">
              {rows.map((row, index) => {
                const isEditing = editingRowId === row.id;
                const typeError = errors[`${row.id}_period_type`];
                const startError = errors[`${row.id}_start_date`];
                const endError = errors[`${row.id}_end_date`];

                return (
                  <div
                    key={row.id}
                    className={`border rounded-xl p-4 transition-colors ${
                      isEditing
                        ? 'bg-blue-50/60 border-blue-200 ring-1 ring-blue-100'
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-gray-500">
                        #{index + 1} · {getTypeLabel(row.period_type) || 'New period'}
                      </span>
                      {!isEditing && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(row.id)}
                            disabled={editingRowId !== null}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-40"
                          >
                            <Icon name="Pencil" size={15} />
                          </button>
                          <button
                            onClick={() => handleDelete(row)}
                            disabled={editingRowId !== null}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-40"
                          >
                            <Icon name="Trash" size={15} />
                          </button>
                        </div>
                      )}
                      {isEditing && (
                        <span className="text-xs font-medium text-blue-700">
                          {row.isNew ? 'New' : 'Editing'}
                        </span>
                      )}
                    </div>

                    {isEditing ? (
                      <div className="space-y-3">
                        {/* Row 1: Type / Status */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Period Type *
                            </label>
                            <select
                              value={row.period_type}
                              onChange={(e) =>
                                handleRowChange(row.id, 'period_type', e.target.value)
                              }
                              className={inputClasses(!!typeError)}
                            >
                              <option value="">Select Type</option>
                              {PERIOD_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            {typeError && <p className="mt-1 text-xs text-red-600">{typeError}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Status
                            </label>
                            <select
                              value={row.status}
                              onChange={(e) => handleRowChange(row.id, 'status', e.target.value)}
                              className={inputClasses(false)}
                            >
                              {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {/* Row 2: Start / End */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Start Date *
                            </label>
                            <input
                              type="date"
                              value={row.start_date}
                              onChange={(e) =>
                                handleRowChange(row.id, 'start_date', e.target.value)
                              }
                              className={inputClasses(!!startError)}
                            />
                            {startError && (
                              <p className="mt-1 text-xs text-red-600">{startError}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              End Date
                            </label>
                            <input
                              type="date"
                              value={row.end_date}
                              onChange={(e) => handleRowChange(row.id, 'end_date', e.target.value)}
                              className={inputClasses(!!endError)}
                            />
                            {endError && <p className="mt-1 text-xs text-red-600">{endError}</p>}
                          </div>
                        </div>
                        {/* Row 3: Remark */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Remark
                          </label>
                          <input
                            type="text"
                            value={row.remark}
                            onChange={(e) => handleRowChange(row.id, 'remark', e.target.value)}
                            className={inputClasses(false)}
                            placeholder="Optional remark"
                          />
                        </div>
                        {/* Actions */}
                        <div className="flex items-end justify-end gap-2 pt-1">
                          <button
                            onClick={() => handleSave(row)}
                            disabled={isSaving}
                            className=" inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-white bg-primary hover:bg-primary-600 text-sm font-medium disabled:opacity-50"
                          >
                            <Icon name="Check" size={15} />
                          </button>
                          <button
                            onClick={() => handleCancelEdit(row)}
                            className="px-3 py-2.5 rounded-lg border border-gray-300 text-sm font-medium bg-white hover:bg-gray-50"
                          >
                            <Icon name="X" size={15} />
                          </button>
                          <button
                            onClick={() => handleDelete(row)}
                            className="p-2.5 rounded-lg text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Icon name="Trash" size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                        <div>
                          <div className="text-[11px] text-gray-500">Start</div>
                          <div className="font-medium text-gray-900">{row.start_date || '-'}</div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500">End</div>
                          <div className="font-medium text-gray-900">{row.end_date || '-'}</div>
                        </div>
                        <div>
                          <div className="text-[11px] text-gray-500">Status</div>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${row.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}
                          >
                            {row.status}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <div className="text-[11px] text-gray-500">Remark</div>
                          <div className="text-gray-700">{row.remark || '-'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeePeriods;
